import i18n from '@/utils/i18n';
import PencilIcon from '@assets/icons/pencil.svg';
import PlusIcon from '@assets/icons/plus.svg';
import React, { useEffect, useMemo, useState } from 'react';
import { ActionSheet } from '@/components/action-sheet';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AddPartitionItemsActionSheet } from './add-partition-items-action-sheet';
import { Button, Props as ButtonProps, SIZES } from '@/components/button';
import { CircledIcon } from '@/components/circled-icon';
import { CONTENT_HEIGHT } from '@/components/navigation-header-content';
import { createStyles } from '@/contexts/theme-context';
import { DuplicateItemsActionSheet } from './duplicate-items-action-sheet';
import { EDIT_ORDER_GUIDE_ROUTES } from '@/navigators/routes';
import { EditCollectionTitle } from './edit-collection-title';
import {
  EditOrderGuideActions,
  Item,
} from '@/hooks/use-edit-order-guide-reducer';
import { EditOrderGuideStackParams } from '@/navigators/types';
import { EditPartitionsActionSheet } from './edit-partitions-action-sheet';
import {
  getSearchFieldsFromMetadata,
  groupItemListOptions,
} from '@/contexts/item-context-utils';
import { ItemSearchHeaderAction } from '@/components/item-search-header-action';
import {
  ItemStateMap,
  OrderGuideEditableRow,
  SortableItemType,
} from './order-guide-editable-row';
import { KeyboardAwareSortableList } from '@/components/sortable-list';
import { ListItem, ListItemSeparator } from '@/components/list-item';
import { ListItemType } from '@/components/recycler-list';
import { MoveItemsActionSheet } from './move-items-action-sheet';
import { Props as NavigationHeaderProps } from '@/components/navigation-header';
import { NO_PARTITION_NAME } from '@/contexts/multi-order-guide-items-context-provider';
import { OrderGuideEditorHeaderRow } from './order-guide-editor-header-row';
import { OrderGuideEditorSaveButton } from './order-guide-editor-save-button';
import { PageContainer } from '@/components/page-container';
import { PartitionFilterType } from '@/contexts/item-context-types';
import {
  PartitionSelector,
  Props as PartitionSelectorProps,
} from '@/components/order-guide/partition-selector';
import { SCREEN_PADDING, SCREEN_PADDING_LARGE } from '@/styles';
import { SearchEmptyState } from './search-empty-state';
import { SearchOrderGuideInput } from './search-order-guide-input';
import { StackNavigationProp } from '@react-navigation/stack';
import {
  useEditOrderGuideActions,
  useEditOrderGuideState,
} from '@/contexts/edit-order-guide-context';
import {
  useItemDisplayOptions,
  useNewOrderContext,
} from '@/contexts/new-order-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useMultiSearchFilters } from '@/hooks/use-multi-search-filters';
import { useNavigation } from '@react-navigation/native';
import { usePrevious } from '@/hooks/use-previous';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View } from 'react-native';

const SMALL_SAVE_BUTTON_HIT_SLOP = {
  bottom: 8,
  left: 0,
  right: 12,
  top: 8,
};

const SMALL_CANCEL_BUTTON_HITSLOP = {
  bottom: 8,
  left: 12,
  right: 0,
  top: 8,
};

const EDIT_BUTTON_HIT_SLOP = {
  bottom: 8,
  left: 0,
  right: 0,
  top: 8,
};

const SmallItemSeparator: React.FC = () => {
  const [styles] = useStyles();
  return <ListItemSeparator style={styles.separatorSmall} />;
};

const createPartitionItemStateMap = (partitionItems: Item[]) => {
  return partitionItems.reduce<ItemStateMap>((result, item) => {
    result[item.itemUUID] = {
      parLevel: item.parLevel,
      selected: false,
    };

    return result;
  }, {});
};

const getSearchableFields = (item: Item) => {
  return getSearchFieldsFromMetadata(item.itemListOption);
};

type Navigation = StackNavigationProp<
  EditOrderGuideStackParams,
  EDIT_ORDER_GUIDE_ROUTES.Base
>;

interface Props {
  canUpdateCollection: boolean;
  collectionNames: string[];
  collectionUUID: string;
  deleteCollectionMode: 'delete' | 'reset' | null;
  loading: boolean;
  onCancelButtonPress: () => void;
  onDelete: () => void;
  onSaved: () => void;
}

export const OrderGuideEditor: React.FC<Props> = ({
  canUpdateCollection,
  collectionNames,
  collectionUUID,
  deleteCollectionMode,
  loading: queryLoading,
  onCancelButtonPress,
  onDelete,
  onSaved,
}) => {
  const itemDisplayOptions = useItemDisplayOptions();
  const { bottom } = useSafeAreaInsets();
  const { customOrderGuideSortEditingEnabled } = useNewOrderContext();
  const { navigate } = useNavigation<Navigation>();
  const { isSmallLayout, isLargeLayout } = useLayoutContext();
  const { partitions, selectedPartitionUUID } = useEditOrderGuideState();
  const [styles, theme] = useStyles();
  const dispatch = useEditOrderGuideActions();
  const previousPartitionUUID = usePrevious(selectedPartitionUUID);

  const [addPartitionItemsSheetOpen, setAddPartitionItemsSheetOpen] =
    useState(false);
  const [duplicateItemsSheetOpen, setDuplicateItemsSheetOpen] = useState(false);
  const [
    removeItemsConfirmationSheetOpen,
    setRemoveItemsConfirmationSheetOpen,
  ] = useState(false);
  const [editPartitionSheetOpen, setEditPartitionSheetOpen] = useState(false);
  const [itemStateMap, setItemStateMap] = useState<ItemStateMap>({});
  const [moveItemsSheetOpen, setMoveItemsSheetOpen] = useState(false);
  const [titleMaxWidth, setTitleMaxWidth] = useState<number>();

  const selectedPartition = useMemo(() => {
    return (
      partitions.find((p) => p.partitionUUID === selectedPartitionUUID) || null
    );
  }, [partitions, selectedPartitionUUID]);

  const { filteredData, searchText, searching, onDisplayTextChange } =
    useMultiSearchFilters<Item>({
      data: selectedPartition?.items || [],
      getSearchableFields,
    });

  const hasSearchText = searchText.length > 0;

  useEffect(() => {
    // Reset item state map when the selected partition changes
    if (selectedPartitionUUID !== previousPartitionUUID) {
      setItemStateMap(
        createPartitionItemStateMap(selectedPartition?.items || []),
      );
    } else {
      // Include any items that weren't already present in the item state map
      // to account for any that were just added
      setItemStateMap((previous) => ({
        ...previous,
        ...createPartitionItemStateMap(
          (selectedPartition?.items || []).filter(
            (item) => !previous[item.itemUUID],
          ),
        ),
      }));
    }
  }, [selectedPartition?.items, selectedPartitionUUID, previousPartitionUUID]);

  const deselectAllItems = () => {
    setItemStateMap((previous) => {
      return Object.keys(previous).reduce<ItemStateMap>((result, itemUUID) => {
        result[itemUUID] = {
          ...previous[itemUUID],
          selected: false,
        };

        return result;
      }, {});
    });
  };

  const selectedPartitionName =
    selectedPartition?.partitionName || NO_PARTITION_NAME;

  const navigationHeaderProps: NavigationHeaderProps = useMemo(() => {
    const sharedPartitionEditProps: ButtonProps = {
      onPress: () => setEditPartitionSheetOpen(true),
      trackingData: {
        name: 'edit_order_guide.edit_partition_button',
        properties: {
          collectionUUID,
        },
      },
    };

    const partitionFilters = partitions.map<PartitionFilterType>(
      (partition) => ({
        metadata: {
          isDefault: partition.isDefault,
        },
        title: partition.partitionName || NO_PARTITION_NAME,
        value: partition.partitionUUID,
      }),
    );

    const sharedPartitionSelectorProps: PartitionSelectorProps = {
      partitionFilter:
        partitionFilters.find(
          (p) => p.value === selectedPartition?.partitionUUID,
        ) || null,
      partitions: partitionFilters,
      setPartitionFilter: (filter) => {
        if (filter) {
          dispatch({
            payload: {
              partitionUUID: String(filter.value),
            },
            type: EditOrderGuideActions.SET_SELECTED_PARTITION,
          });
        }
      },
    };

    const pageTitle = (
      <EditCollectionTitle
        canUpdateCollection={canUpdateCollection}
        collectionNames={collectionNames}
        deleteCollectionMode={deleteCollectionMode}
        onDelete={onDelete}
        style={{ maxWidth: titleMaxWidth }}
      />
    );

    const searchPlaceholder = selectedPartition
      ? i18n.t('search_within_partition_placeholder', {
          partition: selectedPartitionName,
        })
      : '';

    if (isSmallLayout) {
      return {
        content: (
          <View style={styles.navigationContentSmall}>
            <View style={styles.flexOne}>
              <Button
                hitSlop={SMALL_CANCEL_BUTTON_HITSLOP}
                onPress={onCancelButtonPress}
                size="small"
                style={[styles.actionButton, styles.actionButtonLeft]}
                trackingData={{
                  name: 'order_guide_editor.nav_back_button',
                  properties: {
                    collectionUUID,
                  },
                }}
                type="tertiary"
              >
                {i18n.t('cancel')}
              </Button>
            </View>
            <View
              onLayout={(e) => {
                // HACK: on web the ellipsis effect from maxLines doesn't work, so manually
                // set the width to prevent overflow
                setTitleMaxWidth(e.nativeEvent.layout.width);
              }}
              style={styles.navigationTitleWrapper}
            >
              {pageTitle}
            </View>

            <View style={styles.flexOne}>
              <OrderGuideEditorSaveButton
                hitSlop={SMALL_SAVE_BUTTON_HIT_SLOP}
                onSaved={onSaved}
                size="small"
                style={[styles.actionButton, styles.actionButtonRight]}
                type="tertiary"
              />
            </View>
          </View>
        ),
        responsive: true,
        subheader: queryLoading ? null : (
          <>
            <SearchOrderGuideInput
              isLoading={searching}
              onChangeText={onDisplayTextChange}
              placeholder={searchPlaceholder}
              value={searchText}
            />
            <View style={styles.subheaderContainer}>
              <PartitionSelector {...sharedPartitionSelectorProps} />
              <View style={styles.editButtonContainerSmall}>
                <Button
                  {...sharedPartitionEditProps}
                  hitSlop={EDIT_BUTTON_HIT_SLOP}
                  Icon={PencilIcon}
                  iconProps={{ fill: theme.colors.inactiveFill }}
                  size="small"
                  style={styles.editButtonSmall}
                  testID="edit-partitions-button"
                  type="tertiary"
                />
              </View>
            </View>
          </>
        ),
      };
    }

    const baseLargeScreenActions = [
      {
        component: (
          <Button
            containerStyle={styles.cancelButtonContainer}
            onPress={onCancelButtonPress}
            size="medium"
            style={styles.actionButtonLarge}
            trackingData={{
              name: 'edit_order_guide.cancel_button',
              properties: {
                collectionUUID,
              },
            }}
            type="tertiary"
          >
            {i18n.t('cancel')}
          </Button>
        ),
      },
      {
        component: (
          <OrderGuideEditorSaveButton
            onSaved={onSaved}
            size="medium"
            style={styles.actionButtonLarge}
          />
        ),
      },
    ];

    return {
      actions: queryLoading
        ? []
        : isLargeLayout
          ? [
              {
                component: (
                  <ItemSearchHeaderAction
                    isLoading={searching}
                    onSubmit={(value) => {
                      onDisplayTextChange(value || '');
                    }}
                    placeholder={searchPlaceholder}
                    submitOnChange
                  />
                ),
              },
              ...baseLargeScreenActions,
            ]
          : baseLargeScreenActions,
      containerStyle: styles.largeContainerPaddingReset,
      contentStyle: styles.largeContainerContentPadding,
      responsive: true,
      subheader: queryLoading ? null : (
        <>
          {!isLargeLayout ? (
            <SearchOrderGuideInput
              containerStyle={styles.searchTabletContainer}
              isLoading={searching}
              onChangeText={onDisplayTextChange}
              placeholder={searchPlaceholder}
              value={searchText}
            />
          ) : null}
          <View
            style={[
              styles.subheaderContainer,
              styles.largeContainerContentPadding,
              styles.subheaderContainerLarge,
            ]}
          >
            <PartitionSelector
              {...sharedPartitionSelectorProps}
              containerStyle={styles.flexReset}
            />
            <View style={styles.editButtonContainerLarge}>
              <Button {...sharedPartitionEditProps} size="xsmall">
                {i18n.t('edit')}
              </Button>
            </View>
          </View>
        </>
      ),
      title: (
        <View
          onLayout={(e) => {
            setTitleMaxWidth(e.nativeEvent.layout.width);
          }}
          style={styles.flexOne}
        >
          {pageTitle}
        </View>
      ),
    };
  }, [
    deleteCollectionMode,
    canUpdateCollection,
    collectionNames,
    collectionUUID,
    dispatch,
    isLargeLayout,
    isSmallLayout,
    onCancelButtonPress,
    onDelete,
    onDisplayTextChange,
    onSaved,
    partitions,
    queryLoading,
    searching,
    searchText,
    selectedPartition,
    selectedPartitionName,
    styles,
    theme,
    titleMaxWidth,
  ]);

  const listData = useMemo<SortableItemType[]>(() => {
    if (!selectedPartition) {
      return [];
    }

    const variantPackUUIDToItemMap: { [variantPackUUID: string]: Item } = {};
    const ungroupedItemListOptions = filteredData.map((item) => {
      variantPackUUIDToItemMap[item.itemListOption.metadata!.variantPackUUID] =
        item;
      return item.itemListOption;
    });

    return groupItemListOptions(
      ungroupedItemListOptions,
      itemDisplayOptions,
    ).map<SortableItemType>((itemListOption) => {
      const itemForTopLevelVP =
        variantPackUUIDToItemMap[itemListOption.metadata!.variantPackUUID];
      return {
        metadata: {
          skuFields: itemListOption.metadata!.skus.map((sku) => {
            return {
              itemMetadata: sku,
              itemUUID: variantPackUUIDToItemMap[sku.variantPackUUID].itemUUID,
            };
          }),
        },
        title: itemListOption.title,
        value: itemForTopLevelVP.itemUUID,
      };
    });
  }, [itemDisplayOptions, selectedPartition, filteredData]);

  // Note: skuFields already includes the top-level item, so we don't need to add item.value separately
  const visibleItemUUIDs = useMemo(() => {
    return listData.flatMap((item) =>
      (item.metadata?.skuFields || []).map((sku) => sku.itemUUID),
    );
  }, [listData]);

  const [selectedItemUUIDs, hasSelectedAllItems] = useMemo(() => {
    const selectedUUIDs = Object.keys(itemStateMap).filter(
      (itemUUID) => itemStateMap[itemUUID].selected,
    );

    const allVisibleSelected =
      visibleItemUUIDs.length > 0 &&
      visibleItemUUIDs.every((itemUUID) => itemStateMap[itemUUID]?.selected);

    return [selectedUUIDs, allVisibleSelected];
  }, [itemStateMap, visibleItemUUIDs]);

  const selectedItemActions = (
    <>
      <Button
        onPress={() => setMoveItemsSheetOpen(true)}
        trackingData={{
          name: 'order_guide_editor.move_button',
          properties: { partitionUUID: selectedPartitionUUID },
        }}
        type="tertiary"
      >
        {i18n.t('move_to')}
      </Button>
      <Button
        onPress={() => setDuplicateItemsSheetOpen(true)}
        trackingData={{
          name: 'order_guide_editor.duplicate_button',
          properties: { partitionUUID: selectedPartitionUUID },
        }}
        type="tertiary"
      >
        {i18n.t('duplicate')}
      </Button>
      <Button
        onPress={() => setRemoveItemsConfirmationSheetOpen(true)}
        trackingData={{
          name: 'order_guide_editor.remove_button',
          properties: { partitionUUID: selectedPartitionUUID },
        }}
        type="danger"
      >
        {i18n.t('remove')}
      </Button>
    </>
  );

  const navigateToAddNew = (initialSearchText?: string) => {
    if (isSmallLayout) {
      navigate(EDIT_ORDER_GUIDE_ROUTES.AddPartitionItems, {
        initialSearchText,
      });
    } else {
      setAddPartitionItemsSheetOpen(true);
    }
  };

  return (
    <PageContainer navigationHeaderProps={navigationHeaderProps}>
      {queryLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator />
        </View>
      ) : (
        <>
          {listData.length === 0 && hasSearchText ? (
            <SearchEmptyState
              buttonProps={{
                children: i18n.t('add_items_from_catalog'),
                onPress: () => navigateToAddNew(searchText),
                trackingData: {
                  name: 'order_guide_editor.empty_state_button',
                  properties: {
                    collectionUUID,
                  },
                },
              }}
              containerStyle={styles.emptyState}
              searchText={searchText}
            />
          ) : (
            <KeyboardAwareSortableList<SortableItemType>
              // Prevents extra whitespace that renders at the top of the list
              // once the screen receives focus again
              automaticallyAdjustContentInsets={false}
              contentInsetAdjustmentBehavior="automatic"
              disabled={hasSearchText || !customOrderGuideSortEditingEnabled}
              items={listData}
              ItemSeparatorComponent={SmallItemSeparator}
              ListHeaderComponent={() => (
                // ListItem has flex: 1 so wrap in a View to constrain height
                <View collapsable={false}>
                  <ListItem
                    item={{
                      title: i18n.t('add_items_to_group', {
                        groupName: selectedPartitionName,
                      }),
                      value: ListItemType.Creatable,
                    }}
                    listItemStyle={[styles.listItem, styles.addItemListItem]}
                    onSelect={() => navigateToAddNew()}
                    renderLeftComponent={() => (
                      <View style={styles.addIconContainer}>
                        <CircledIcon
                          Icon={PlusIcon}
                          iconHeight={20}
                          iconWidth={20}
                          size="small"
                        />
                      </View>
                    )}
                    renderRightComponent={() => {
                      if (selectedItemUUIDs.length > 0 && !isSmallLayout) {
                        return (
                          <View style={styles.selectedItemActionsLarge}>
                            {selectedItemActions}
                          </View>
                        );
                      }
                    }}
                  />
                  {listData.length > 0 ? (
                    <OrderGuideEditorHeaderRow
                      isSelectAllSelected={hasSelectedAllItems}
                      onSelectAll={() => {
                        setItemStateMap((previous) => {
                          const result = { ...previous };
                          // toggle selection for visible items
                          visibleItemUUIDs.forEach((itemUUID) => {
                            if (result[itemUUID]) {
                              result[itemUUID] = {
                                ...result[itemUUID],
                                selected: !hasSelectedAllItems,
                              };
                            }
                          });
                          return result;
                        });
                      }}
                      partitionUUID={selectedPartitionUUID!}
                    />
                  ) : null}
                </View>
              )}
              listItemHorizontalPadding={16}
              onChange={(items) => {
                if (selectedPartitionUUID) {
                  dispatch({
                    payload: {
                      partitionUUID: selectedPartitionUUID,
                      sortedItemUUIDs: items.flatMap((item) =>
                        (item.metadata?.skuFields || []).map(
                          (sku) => sku.itemUUID,
                        ),
                      ),
                    },
                    type: EditOrderGuideActions.SORT_ITEMS,
                  });
                }
              }}
              renderItemContent={(item, index) => {
                return (
                  <OrderGuideEditableRow
                    index={index}
                    item={item}
                    itemStateMap={itemStateMap}
                    onSelect={(itemUUID) => {
                      setItemStateMap((previous) => ({
                        ...previous,
                        [itemUUID]: {
                          ...previous[itemUUID],
                          selected: !previous[itemUUID].selected,
                        },
                      }));
                    }}
                    partitionUUID={selectedPartitionUUID!}
                  />
                );
              }}
              stickyHeaderIndices={[0]}
            />
          )}
          {selectedItemUUIDs.length > 0 && isSmallLayout ? (
            <View
              style={[styles.selectedItemActions, { paddingBottom: bottom }]}
            >
              {selectedItemActions}
            </View>
          ) : null}
          {addPartitionItemsSheetOpen ? (
            <AddPartitionItemsActionSheet
              initialSearchText={searchText}
              onClose={() => setAddPartitionItemsSheetOpen(false)}
              selectedPartition={selectedPartition!}
            />
          ) : null}
          {moveItemsSheetOpen ? (
            <MoveItemsActionSheet
              onClose={(deselectOnComplete: boolean) => {
                if (deselectOnComplete) {
                  deselectAllItems();
                }
                setMoveItemsSheetOpen(false);
              }}
              partitions={partitions}
              selectedItemUUIDs={selectedItemUUIDs}
              selectedPartitionName={selectedPartitionName}
              selectedPartitionUUID={selectedPartitionUUID!}
              sortingEnabled={customOrderGuideSortEditingEnabled}
            />
          ) : null}
          {duplicateItemsSheetOpen ? (
            <DuplicateItemsActionSheet
              onClose={() => setDuplicateItemsSheetOpen(false)}
              partitions={partitions}
              selectedItemUUIDs={selectedItemUUIDs}
              selectedPartitionName={selectedPartitionName}
              selectedPartitionUUID={selectedPartitionUUID!}
            />
          ) : null}
          <ActionSheet
            actions={[i18n.t('remove'), i18n.t('cancel')]}
            isVisible={removeItemsConfirmationSheetOpen}
            onAction={(_, i) => {
              if (i === 0) {
                dispatch({
                  payload: {
                    itemUUIDs: Object.keys(itemStateMap).filter(
                      (itemUUID) => itemStateMap[itemUUID].selected,
                    ),
                    partitionUUID: selectedPartitionUUID!,
                  },
                  type: EditOrderGuideActions.REMOVE_ITEMS,
                });
                deselectAllItems();
              }

              setRemoveItemsConfirmationSheetOpen(false);
            }}
            onClose={() => setRemoveItemsConfirmationSheetOpen(false)}
            title={i18n.t('remove_items_modal.title', {
              groupName: selectedPartitionName,
            })}
          />
          {editPartitionSheetOpen ? (
            <EditPartitionsActionSheet
              onClose={() => setEditPartitionSheetOpen(false)}
              partitions={partitions}
            />
          ) : null}
        </>
      )}
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  actionButton: {
    paddingHorizontal: 0,
  },
  actionButtonLarge: {
    width: 130,
  },
  actionButtonLeft: {
    alignSelf: 'flex-start',
  },
  actionButtonRight: {
    alignSelf: 'flex-end',
  },
  addIconContainer: {
    justifyContent: 'center',
    // Match OrderGuideEditorItemActions button height to prevent layout from
    // shifting when the actions appear
    minHeight: SIZES.medium,
  },
  addItemListItem: {
    paddingVertical: 8,
  },
  cancelButtonContainer: {
    marginRight: 16,
  },
  editButtonContainerLarge: {
    justifyContent: 'center',
    marginLeft: 16,
  },
  editButtonContainerSmall: {
    borderLeftColor: theme.colors.stroke,
    borderLeftWidth: 1,
    justifyContent: 'center',
  },
  editButtonSmall: {
    width: 40,
  },
  emptyState: {
    justifyContent: 'flex-start',
    marginTop: 40,
  },
  flexOne: {
    flex: 1,
  },
  flexReset: {
    flex: -1,
  },
  largeContainerContentPadding: {
    paddingHorizontal: SCREEN_PADDING_LARGE,
  },
  largeContainerPaddingReset: {
    paddingHorizontal: 0,
  },
  listItem: {
    paddingHorizontal: 16,
  },
  loadingContainer: {
    marginTop: SCREEN_PADDING,
  },
  navigationContentSmall: {
    alignItems: 'center',
    flexDirection: 'row',
    height: CONTENT_HEIGHT,
  },
  navigationTitleWrapper: {
    alignItems: 'center',
    flex: 3,
    justifyContent: 'center',
  },
  searchTabletContainer: {
    marginHorizontal: SCREEN_PADDING_LARGE,
  },
  selectedItemActions: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  selectedItemActionsLarge: {
    flexDirection: 'row',
  },
  separatorSmall: {
    marginHorizontal: 16,
  },
  subheaderContainer: {
    backgroundColor: theme.colors.white,
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  subheaderContainerLarge: {
    justifyContent: 'flex-start',
  },
}));
